import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../styles/MyReturns.css";

const STATUS_OPTIONS = [
  "ALL",
  "REQUESTED",
  "APPROVED",
  "AWAITING_SHIPMENT",
  "RECEIVED",
  "REFUND_PENDING",
  "REFUNDED",
  "REJECTED",
  "CANCELLED",
];

export default function MyReturns() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [returns, setReturns] = useState([]);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadReturns();
  }, []);

  const loadReturns = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch("/api/returns", {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("RETURNS_LOAD_FAILED");
      }

      const data = await response.json();

      setReturns(Array.isArray(data) ? data : []);
    } catch {
      setError(t("returns.listLoadError"));
    } finally {
      setLoading(false);
    }
  };

  const filteredReturns = useMemo(() => {
    if (statusFilter === "ALL") {
      return returns;
    }

    return returns.filter(
      (returnRequest) =>
        returnRequest.status === statusFilter
    );
  }, [returns, statusFilter]);

  const formatDate = (value) => {
    if (!value) {
      return "—";
    }

    return new Date(value).toLocaleDateString();
  };

  const formatAmount = (value) => {
    return Number(value || 0).toFixed(2);
  };

  return (
    <main className="my-returns-page">
      <section className="my-returns-container">
        <button
          type="button"
          className="profile-back-btn"
          onClick={() => navigate("/profile")}
        >
          ← {t("returns.backToProfile")}
        </button>

        <div className="my-returns-heading">
          <div>
            <h1>{t("returns.myReturnsTitle")}</h1>
            <p>{t("returns.myReturnsSubtitle")}</p>
          </div>

          <button
            type="button"
            className="my-returns-refresh"
            onClick={loadReturns}
            disabled={loading}
          >
            {loading
              ? t("returns.loading")
              : t("returns.refresh")}
          </button>
        </div>

        <div className="my-returns-filter">
          <label>
            {t("returns.filterByStatus")}

            <select
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(event.target.value)
              }
            >
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status === "ALL"
                    ? t("returns.allStatuses")
                    : t(`returns.status.${status}`)}
                </option>
              ))}
            </select>
          </label>
        </div>

        {error && (
          <section className="my-returns-error">
            <p>{error}</p>

            <button type="button" onClick={loadReturns}>
              {t("returns.retry")}
            </button>
          </section>
        )}

        {!error && loading && (
          <section className="my-returns-loading">
            {t("returns.loading")}
          </section>
        )}

        {!error &&
          !loading &&
          filteredReturns.length === 0 && (
            <section className="my-returns-empty">
              <h2>{t("returns.noReturnsTitle")}</h2>
              <p>{t("returns.noReturnsText")}</p>

              <button
                type="button"
                onClick={() => navigate("/orders")}
              >
                {t("returns.viewOrders")}
              </button>
            </section>
          )}

        {!error &&
          !loading &&
          filteredReturns.length > 0 && (
            <div className="my-returns-list">
              {filteredReturns.map((returnRequest) => (
                <article
                  className="my-return-card"
                  key={returnRequest.id}
                  onClick={() =>
                    navigate(`/returns/${returnRequest.id}`)
                  }
                >
                  <div className="my-return-card-main">
                    <div>
                      <h2>{returnRequest.returnNumber}</h2>

                      <p>
                        {t("returns.orderLabel")}:{" "}
                        <strong>
                          {returnRequest.orderNumber}
                        </strong>
                      </p>
                    </div>

                    <span
                      className={`my-return-status ${returnRequest.status
                        ?.toLowerCase()
                        .replaceAll("_", "-")}`}
                    >
                      {t(
                        `returns.status.${returnRequest.status}`
                      )}
                    </span>
                  </div>

                  <div className="my-return-card-details">
                    <div>
                      <span>{t("returns.requestDate")}</span>
                      <strong>
                        {formatDate(returnRequest.requestedAt)}
                      </strong>
                    </div>

                    <div>
                      <span>{t("returns.products")}</span>
                      <strong>
                        {returnRequest.items?.length || 0}
                      </strong>
                    </div>

                    <div>
                      <span>{t("returns.refundAmount")}</span>
                      <strong>
                        {formatAmount(
                          returnRequest.refundAmount
                        )}{" "}
                        €
                      </strong>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="my-return-detail-btn"
                    onClick={(event) => {
                      event.stopPropagation();

                      navigate(
                        `/returns/${returnRequest.id}`
                      );
                    }}
                  >
                    {t("returns.viewDetail")}
                  </button>
                </article>
              ))}
            </div>
          )}
      </section>
    </main>
  );
}